import React from 'react';
import type { CompositeFilter } from '@sbt-web/network/types';
import { Category } from '@sbt-web/category-utils';

import type { FiltersStore } from '@tools/search/getFiltersStore';

import {
  AdTypeSelector,
  BoolSelector,
  ListSelector,
  QSOSelector,
  RangeSelector,
  ShippableOnlySelector,
  FilterableListSelector,
} from '../Elements/Filters';
import { UrgentSelector } from '../Elements/Filters/UrgentSelector';
import { FilterURI } from '@sbt-web/network/types';
import { extractDependencyValues } from '@shared/helpers/DataSourceHelpers';

type FilterProps = {
  filter: CompositeFilter;
  category: Category;
  onFilterSelected: (key: string, value?: string, label?: string) => void;
  activeFilters: { [key: string]: string };
  trackSearchConversion: () => void;
  filterStore: FiltersStore;
};

export const Filter = ({
  filter,
  category,
  onFilterSelected,
  activeFilters,
  trackSearchConversion,
  filterStore,
}: FilterProps) => {
  // Special Cases
  // ! remember to update SPECIAL_FILTERS in src/shared/tools/search/getFiltersStore.ts when any of these are removed
  switch (filter.uri) {
    // remove this special case when possible
    case FilterURI.ItemShippable:
      return (
        <ShippableOnlySelector trackSearchConversion={trackSearchConversion} />
      );
    case FilterURI.AdType:
      return (
        <AdTypeSelector
          adTypes={category.adTypes}
          trackSearchConversion={trackSearchConversion}
        />
      );
    // remove this special case when possible
    case FilterURI.ItemUrgent:
      return <UrgentSelector trackSearchConversion={trackSearchConversion} />;
    case FilterURI.QuerySubjectOnly:
      return <QSOSelector trackSearchConversion={trackSearchConversion} />;
  }
  // End Special Cases

  switch (filter.type) {
    case 'bool':
      return (
        <BoolSelector
          filter={filter}
          onSelect={onFilterSelected}
          initialValue={activeFilters[filter.queryString]}
        />
      );
    case 'list': {
      const dependenciesValues = extractDependencyValues(
        filter,
        filterStore,
        activeFilters
      );

      if (filter.searchable)
        return (
          <FilterableListSelector
            filter={filter}
            category={category}
            onSelect={onFilterSelected}
            initialValue={activeFilters[filter.queryString]}
            dependenciesValues={dependenciesValues}
          />
        );

      return (
        <ListSelector
          filter={filter}
          category={category}
          onSelect={onFilterSelected}
          initialValue={activeFilters[filter.queryString]}
          dependenciesValues={dependenciesValues}
        />
      );
    }
    case 'range':
      return (
        <RangeSelector
          filter={filter}
          onSelect={onFilterSelected}
          initialValues={{
            min: activeFilters[filter.min.queryString],
            max: activeFilters[filter.max.queryString],
          }}
        />
      );
    default:
      return null;
  }
};
